import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { getPublicBrands } from '../../../api/brand/brandAction';
import { rememberOffer } from '../../../utils/offerSession';
import { fetchPublicOffers } from '../../../api/offers/offersApi';
import { track } from '../../../utils/tracker';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import GridViewIcon from '@mui/icons-material/GridView';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircle from '@mui/icons-material/AccountCircle';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import StarIcon from '@mui/icons-material/Star';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from 'react-router-dom';
import { PUBLIC_BRAND_RED_OLIVE, PUBLIC_BRAND_STAYJADE } from '../../../constants/publicBrands';
import {
  buildBrandListingHref,
  getCanonicalBrandListingPath,
} from '../../../views/property-list/listingBranding';
import { customerLogout } from '../../../utils/customerSession';

const PROMO_BG = 'linear-gradient(90deg, #0D1F3C 0%, #1a3a6e 40%, #8B1a2e 70%, #E8304A 100%)';
const PROMO_ACCENT = '#FFBD59';

const navItems = [
    { label: "Our Brands", url: "/properties" },
    { label: "Collections", url: "/properties" }
];

/** Fallback menu when /brand/get has not loaded yet or returns empty */
const FALLBACK_BRAND_MENU = [
    {
        name: "StayJade",
        desc: "Leisure Villas",
        url: buildBrandListingHref(PUBLIC_BRAND_STAYJADE),
        logo: "https://www.stayjade.com/assets/logo-CQAXgZpE.png",
        bg: "linear-gradient(135deg, #050403 0%, #0D0A06 40%, #1C1408 70%, #2A1E08 100%)",
        border: "1px solid rgba(184,134,42,0.18)"
    },
    {
        name: "Red Olive",
        desc: "Urban Stays",
        url: buildBrandListingHref(PUBLIC_BRAND_RED_OLIVE),
        logo: "https://www.theredolive.co/images/newlogo.png",
        bg: "linear-gradient(135deg, #0A0404 0%, #1A0606 45%, #3D0808 75%, #6B0A0A 100%)",
        border: "1px solid rgba(180,20,20,0.3)"
    }
];

function NavbarComponent({
    isLoggedIn,
    loggedCustomer,
    appBarBackground,
    publicBrands = [],
    getPublicBrands,
    promoBanner,
}) {
    const navigate = useNavigate();

    useEffect(() => {
        // Always refresh — Redux can hold a stale "Test" brand from an earlier session
        getPublicBrands();
    }, [getPublicBrands]);

    const menuBrands = useMemo(() => {
        if (publicBrands?.length) {
            return publicBrands.map((b, i) => {
                const slugOrKey = (b.slug && String(b.slug).trim()) ||
                    String(b.name || '').trim().toLowerCase().replace(/\s+/g, '-');
                const nameKey = String(b.name || '').toLowerCase().replace(/\s+/g, '');
                const styleSeed =
                    nameKey.includes('stayjade') || String(b.slug || '').includes('stayjade')
                        ? FALLBACK_BRAND_MENU[0]
                        : nameKey.includes('redolive') || String(b.slug || '').includes('red')
                          ? FALLBACK_BRAND_MENU[1]
                          : FALLBACK_BRAND_MENU[i % FALLBACK_BRAND_MENU.length];
                return {
                    key: b.id ?? `brand-${i}`,
                    name: b.name,
                    desc: (b.description && String(b.description).slice(0, 90)) || styleSeed.desc || 'Explore properties',
                    url: getCanonicalBrandListingPath(slugOrKey) ||
                        `/properties?brand=${encodeURIComponent(slugOrKey)}`,
                    logo: b.logoWithBg || b.logoTransparent || styleSeed.logo || '',
                    bg: styleSeed.bg,
                    border: styleSeed.border,
                };
            });
        }
        return FALLBACK_BRAND_MENU.map((b) => ({ ...b, key: b.name }));
    }, [publicBrands]);
    const [anchorEll, setAnchorEll] = React.useState(null);
    const [brandsAnchorEl, setBrandsAnchorEl] = React.useState(null);
    const [loginAnchorEl, setLoginAnchorEl] = React.useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileBrandsOpen, setMobileBrandsOpen] = useState(false);
    const [liveOfferCount, setLiveOfferCount] = useState(0);
    useEffect(() => {
        let alive = true;
        fetchPublicOffers().then((list) => alive && setLiveOfferCount(list.length));
        return () => { alive = false; };
    }, []);
    const [selectedItem, setSelectedItem] = useState(null);

    const handleGuestLogin = () => navigate('/login');
    const handleOwnerLogin = () => window.open('https://owner.jadecaps.com/jadecaps/login', '_blank', 'noopener,noreferrer');
    const handleLogout = () => {
        handleClose();
        closeLoginMenu();
        customerLogout('/');
    };

    const handleMenu = (event) => {
        setAnchorEll(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEll(null);
    };
    const closeBrandsMenu = () => setBrandsAnchorEl(null);
    const closeLoginMenu = () => setLoginAnchorEl(null);

    const handleDrawerToggle = () => {
        setMobileOpen((open) => {
            if (open) setMobileBrandsOpen(false);
            return !open;
        });
    };

    const handleSelectItem = (item) => {
        setSelectedItem(item);
        setMobileOpen(false);
    };

    const promoVisible = Boolean(promoBanner?.visible);
    const promoText = promoBanner?.text || '';
    const promoCode = promoBanner?.code || '';
    const promoLink = promoBanner?.link ? String(promoBanner.link).trim() : '';
    const promoIsExternal = /^https?:\/\//i.test(promoLink);
    // Offer strips link to /offers/:code — route in-app and carry the code to checkout.
    const onPromoClick = (e) => {
        if (promoBanner?.offerCode) {
            rememberOffer(promoBanner.offerCode);
            track('offer_click', { code: promoBanner.offerCode, place: 'strip' });
        }
        if (promoLink && !promoIsExternal) {
            e.preventDefault();
            navigate(promoLink);
        }
    };

    return (
        <>
            {promoVisible && (
            <Box
                className="site-promo-banner"
                component={promoLink ? 'a' : 'div'}
                href={promoLink || undefined}
                target={promoLink && promoIsExternal ? '_blank' : undefined}
                rel={promoLink && promoIsExternal ? 'noopener noreferrer' : undefined}
                onClick={promoLink ? onPromoClick : undefined}
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1301,
                    background: PROMO_BG,
                    color: "#fff",
                    textAlign: "center",
                    px: { xs: 1, sm: 1.5, md: 3 },
                    py: { xs: "0.45rem", md: "0.6rem" },
                    minHeight: { xs: 52, md: 40 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "DM Sans",
                    fontSize: { xs: "0.68rem", sm: "0.72rem", md: "0.82rem" },
                    fontWeight: 500,
                    letterSpacing: "0.01em",
                    lineHeight: { xs: 1.3, md: 1.4 },
                    textDecoration: "none",
                    cursor: promoLink ? "pointer" : "default",
                }}
            >
                <Box
                    component="span"
                    sx={{
                        px: 0.25,
                        lineHeight: { xs: 1.3, md: 1.4 },
                        whiteSpace: { xs: "normal", sm: "nowrap" },
                    }}
                >
                    {promoText}
                    {promoCode ? (
                        <>
                            {promoText ? ' ' : ''}
                            <Box component="strong" sx={{ color: PROMO_ACCENT, fontWeight: 700 }}>
                                {promoCode}
                            </Box>
                        </>
                    ) : null}
                </Box>
            </Box>
            )}
            <AppBar
                position="fixed"
                sx={{
                    top: promoVisible ? { xs: "52px", md: "40px" } : 0,
                    zIndex: 1300,
                    background: "#ffffff",
                    boxShadow: "0 1px 16px rgba(13,31,60,0.06)",
                    borderBottom: "1px solid rgba(226,230,238,0.5)",
                    py: 0,
                    height: { xs: 68, md: 68 },
                    justifyContent: "center",
                    overflow: "visible",
                }}
            >
                <Toolbar
                    sx={{
                        justifyContent: "space-between",
                        width: "100%",
                        maxWidth: "1280px",
                        minHeight: { xs: 68, md: 68 },
                        px: { xs: 2, md: 4 },
                    }}
                >
                    <Box
                        sx={{
                            // background: "linear-gradient(135deg, #0F2437 0%, #0D1F3C 60%, #153357 100%)",
                            borderRadius: "10px",
                            // px: 1.2,
                            // py: 0.6,
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer"
                        }}
                        onClick={() => navigate("/")}
                    >
                        <Box
                            component="img"
                            src={`${process.env.PUBLIC_URL}/images/image/logo1.png`}
                            alt="Logo"
                            sx={{
                                width: "auto",
                                height: { xs: "58px", md: "100px" },
                                maxWidth: { xs: "200px", md: "4800px" },
                                display: "block",
                                objectFit: "contain",
                            }}
                        />
                    </Box>

                    {/* Right: nav + account + menu + logout (logout last) */}
                    <Box
                        sx={{
                            ml: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.35, md: 0.25 },
                            flexShrink: 0,
                            position: 'relative',
                            zIndex: 3,
                        }}
                    >
                    <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}>
                        {navItems.map((item) => (
                            <Typography
                                key={item.label}
                                sx={{
                                    color: "#374151",
                                    textTransform: "none",
                                    mx: 1,
                                    fontFamily: "DM Sans",
                                    fontSize: "0.84rem",
                                    cursor: "pointer",
                                    fontWeight: selectedItem === item ? 600 : 400,
                                    padding: item.bg ? "10px 20px" : "auto",
                                    background: item.bg ? "#0D1B2A" : "transparent",
                                    borderRadius: "7px",
                                    "&:hover": {
                                        color: "#0D1F3C",
                                        background: "rgba(13,31,60,0.08)"
                                    }
                                }}
                                onMouseEnter={(e) => {
                                    if (item.label === "Our Brands") setBrandsAnchorEl(e.currentTarget);
                                }}
                                onClick={() => {
                                    if (item.label !== "Our Brands") {
                                        handleSelectItem(item);
                                        navigate(item?.url);
                                    }
                                }}
                            >
                                {item?.label}
                            </Typography>
                        ))}
                        {/* Offers — always visible, with a live count from Coupons */}
                        <Box
                            component="button"
                            type="button"
                            onClick={() => navigate("/offers")}
                            sx={{
                                mx: 1,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.6,
                                border: "1px solid rgba(232,48,74,0.25)",
                                background: "linear-gradient(135deg, #fff4e0 0%, #ffe3e8 100%)",
                                color: "#B4232F",
                                fontFamily: "DM Sans",
                                fontSize: "0.82rem",
                                fontWeight: 700,
                                padding: "0.38rem 0.85rem",
                                borderRadius: "999px",
                                cursor: "pointer",
                                transition: "transform .15s, box-shadow .15s",
                                "&:hover": { transform: "translateY(-1px)", boxShadow: "0 6px 16px rgba(232,48,74,0.18)" },
                            }}
                        >
                            <span aria-hidden="true">🔥</span>
                            Offers
                            {liveOfferCount > 0 && (
                                <Box component="span" sx={{ minWidth: 18, height: 18, px: 0.6, borderRadius: "999px", background: "#E8304A", color: "#fff", fontSize: "0.66rem", fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                                    {liveOfferCount}
                                </Box>
                            )}
                        </Box>
                        <Menu
                            anchorEl={brandsAnchorEl}
                            open={Boolean(brandsAnchorEl)}
                            onClose={closeBrandsMenu}
                            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                            transformOrigin={{ vertical: "top", horizontal: "left" }}
                            MenuListProps={{
                                onMouseLeave: closeBrandsMenu
                            }}
                            PaperProps={{
                                sx: {
                                    mt: 1,
                                    minWidth: 320,
                                    border: "1px solid #e2e6ee",
                                    borderRadius: "14px",
                                    boxShadow: "0 16px 48px rgba(13,31,60,0.12)",
                                    p: 0.8
                                }
                            }}
                        >
                            {menuBrands.map((brand) => (
                                <MenuItem
                                    key={brand.key}
                                    onClick={() => {
                                        navigate(brand.url);
                                        closeBrandsMenu();
                                    }}
                                    sx={{
                                        borderRadius: "12px",
                                        mb: 0.6,
                                        p: 0,
                                        overflow: "hidden",
                                        background: brand.bg,
                                        border: brand.border,
                                        color: "#fff",
                                        "&:hover": { filter: "brightness(1.06)" }
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, width: "100%", px: 1.2, py: 1.1 }}>
                                        <Box sx={{ width: 38, height: 38, borderRadius: "8px", overflow: "hidden", background: "rgba(0,0,0,0.2)", display: "grid", placeItems: "center" }}>
                                            {brand.logo ? (
                                                <img
                                                  src={brand.logo}
                                                  alt={brand.name}
                                                  width={38}
                                                  height={38}
                                                  decoding="async"
                                                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                />
                                            ) : (
                                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#fff" }}>{(brand.name || '?').slice(0, 2)}</Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ lineHeight: 1.2 }}>
                                            <Box sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#fff" }}>{brand.name}</Box>
                                            <Box sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.72)" }}>{brand.desc}</Box>
                                        </Box>
                                    </Box>
                                </MenuItem>
                            ))}
                            <Box sx={{ px: 1.2, py: 0.9, borderTop: "1px solid #e2e6ee", display: "flex", alignItems: "center", gap: 0.7, background: "linear-gradient(135deg, #FFF8EC, #fff)" }}>
                                <Box sx={{ width: 24, height: 24, borderRadius: "6px", background: "linear-gradient(135deg, #FFBD59, #ffd78f)", display: "grid", placeItems: "center" }}>
                                    <StarIcon sx={{ fontSize: 14, color: "#0D1F3C" }} />
                                </Box>
                                <Typography sx={{ fontSize: "0.72rem", color: "#6B7280", fontFamily: "DM Sans" }}>
                                    Listed on <strong style={{ color: "#9A7020" }}>Homes &amp; Villas by Marriott Bonvoy</strong>
                                </Typography>
                            </Box>
                        </Menu>

                        {!isLoggedIn && (
                            <>
                        <Typography
                            sx={{
                                color: "#374151",
                                textTransform: "none",
                                mx: 1,
                                fontFamily: "DM Sans",
                                fontSize: "0.8rem",
                                cursor: "pointer",
                                fontWeight: "normal",
                                padding: "0.4rem 0.875rem",
                                borderRadius: "7px",
                                "&:hover": {
                                    color: "#0D1F3C",
                                    background: "rgba(13,31,60,0.08)"
                                }
                            }}
                            onClick={() => navigate("/list-your-property")}
                        >
                            List Your Property
                        </Typography>
                        <Typography
                            sx={{
                                color: "#374151",
                                textTransform: "none",
                                mx: 1,
                                fontFamily: "DM Sans",
                                fontSize: "0.8rem",
                                cursor: "pointer",
                                fontWeight: "normal",
                                padding: "0.4rem 0.875rem",
                                borderRadius: "7px",
                                "&:hover": {
                                    color: "#0D1F3C",
                                    background: "rgba(13,31,60,0.08)"
                                }
                            }}
                            onClick={() => navigate("/travel-agents")}
                        >
                            Travel Agents
                        </Typography>
                        <Typography
                            sx={{
                                color: "#374151",
                                textTransform: "none",
                                mx: 1,
                                fontFamily: "DM Sans",
                                fontSize: "0.8rem",
                                cursor: "pointer",
                                fontWeight: "normal",
                                padding: "0.4rem 0.875rem",
                                borderRadius: "7px",
                                "&:hover": {
                                    color: "#0D1F3C",
                                    background: "rgba(13,31,60,0.08)"
                                }
                            }}
                            onClick={() => navigate("/contact-us")}
                        >
                            Contact Us
                        </Typography>
                            </>
                        )}

                        {isLoggedIn && (
                            <Typography
                                sx={{
                                    color: "#374151",
                                    textTransform: "none",
                                    mx: 1,
                                    fontFamily: "DM Sans",
                                    fontSize: "0.8rem",
                                    cursor: "pointer",
                                    fontWeight: "normal",
                                    padding: "0.4rem 0.875rem",
                                    borderRadius: "7px"
                                }}
                                onClick={() => navigate("/my-booking")}
                            >
                                My Booking
                            </Typography>
                        )}

                        {!isLoggedIn && (
                            <Typography
                                sx={{
                                    color: "#ffffff",
                                    textTransform: "none",
                                    mx: 1,
                                    fontFamily: "DM Sans",
                                    fontSize: "0.8rem",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                    padding: "0.5rem 1.375rem",
                                    background: "#E8304A",
                                    borderRadius: "8px",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        background: "#d72742",
                                        transform: "translateY(-1px)",
                                        boxShadow: "0 4px 12px rgba(232, 48, 74, 0.3)"
                                    }
                                }}
                                onClick={(e) => setLoginAnchorEl(e.currentTarget)}
                            >
                                Login
                            </Typography>
                        )}
                        <Menu
                            anchorEl={loginAnchorEl}
                            open={Boolean(loginAnchorEl)}
                            onClose={closeLoginMenu}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                        >
                            <MenuItem
                                onClick={() => {
                                    handleGuestLogin();
                                    closeLoginMenu();
                                }}
                            >
                                <PersonOutlineIcon sx={{ mr: 1, fontSize: 19, color: "#0D1F3C" }} />
                                Guest Login
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    handleOwnerLogin();
                                    closeLoginMenu();
                                }}
                            >
                                <HomeWorkOutlinedIcon sx={{ mr: 1, fontSize: 19, color: "#0D1F3C" }} />
                                For Owner Login
                            </MenuItem>
                        </Menu>

                        {isLoggedIn && (
                            <>
                                <IconButton
                                    size="large"
                                    aria-label="Account menu"
                                    aria-controls="menu-appbar"
                                    aria-haspopup="true"
                                    onClick={handleMenu}
                                    sx={{ color: '#374151', display: { xs: 'none', md: 'inline-flex' } }}
                                >
                                    <AccountCircle />
                                    <Typography sx={{ fontSize: '0.95rem', fontFamily: 'DM Sans', color: '#374151' }}>
                                        &nbsp;{loggedCustomer?.firstName}
                                    </Typography>
                                </IconButton>

                                <Menu
                                    id="menu-appbar"
                                    anchorEl={anchorEll}
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    keepMounted
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    open={Boolean(anchorEll)}
                                    onClose={handleClose}
                                >
                                    <MenuItem onClick={() => { handleClose(); navigate('/profile'); }}>Profile</MenuItem>
                                    <MenuItem onClick={() => { handleClose(); navigate('/my-booking'); }}>My bookings</MenuItem>
                                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>

                    <IconButton
                        edge="end"
                        color="inherit"
                        aria-label="menu"
                        onClick={handleDrawerToggle}
                        sx={{
                            display: { xs: "flex", md: "none" },
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#374151",
                            backgroundColor: "rgba(13,31,60,0.08)",
                            borderRadius: "16px",
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                            position: "relative",
                            zIndex: 4,
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            boxShadow: "0 8px 20px rgba(13,31,60,0.15)",
                            '&:hover': {
                                backgroundColor: "rgba(255,255,255,0.24)"
                            }
                        }}
                    >
                        <GridViewIcon fontSize="small" />
                    </IconButton>

                    {isLoggedIn && (
                        <Tooltip title="Log out">
                            <IconButton
                                aria-label="Log out"
                                onClick={handleLogout}
                                sx={{
                                    color: '#374151',
                                    ml: { xs: 0, md: 0.25 },
                                    '&:hover': {
                                        color: '#E8304A',
                                        background: 'rgba(232, 48, 74, 0.08)',
                                    },
                                }}
                            >
                                <LogoutOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Mobile drawer */}
            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ sx: { zIndex: 1500 } }}
                PaperProps={{
                    sx: {
                        width: '70vw',
                        maxWidth: '300px',
                        boxSizing: 'border-box',
                        zIndex: 1500,
                        bgcolor: "rgba(255,255,255,0.98)",
                        color: "#fff",
                        backdropFilter: "blur(18px)",
                        WebkitBackdropFilter: "blur(18px)",
                        borderLeft: "1px solid rgba(226,230,238,0.8)",
                        top: 0,
                        height: '100%',
                    },
                }}
                sx={{
                    zIndex: 1500,
                    '& .MuiBackdrop-root': {
                        zIndex: 1499,
                    },
                }}
            >
                <List sx={{ pt: 2 }}>
                    <ListItem sx={{ justifyContent: 'flex-end', mb: 2 }}>
                        <IconButton onClick={handleDrawerToggle} sx={{ color: "#374151" }}>
                            <CloseIcon />
                        </IconButton>
                    </ListItem>

                    {navItems.map((item, ind) => (
                        <React.Fragment key={ind}>
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => {
                                        if (item.label === "Our Brands") {
                                            setMobileBrandsOpen((open) => !open);
                                            return;
                                        }
                                        navigate(item?.url);
                                        handleDrawerToggle();
                                    }}
                                    sx={{
                                        py: 1.5,
                                        borderBottom: '1px solid rgba(226,230,238,0.7)',
                                    }}
                                >
                                    <ListItemText
                                        primary={item?.label}
                                        primaryTypographyProps={{
                                            fontFamily: "DM Sans",
                                            color: "#374151",
                                            fontSize: "16px",
                                            fontWeight: item.label === "Our Brands" ? 600 : 400,
                                        }}
                                    />
                                    {item.label === "Our Brands" && (
                                        mobileBrandsOpen
                                            ? <ExpandLess sx={{ color: "#374151" }} />
                                            : <ExpandMore sx={{ color: "#374151" }} />
                                    )}
                                </ListItemButton>
                            </ListItem>

                            {item.label === "Our Brands" && (
                                <Collapse in={mobileBrandsOpen} timeout="auto" unmountOnExit>
                                    <List disablePadding sx={{ bgcolor: "rgba(13,31,60,0.04)" }}>
                                        {menuBrands.map((b) => (
                                            <ListItem key={b.key} disablePadding>
                                                <ListItemButton
                                                    onClick={() => {
                                                        navigate(b.url);
                                                        handleDrawerToggle();
                                                    }}
                                                    sx={{
                                                        py: 1.15,
                                                        pl: 3.5,
                                                        borderBottom: '1px solid rgba(226,230,238,0.5)',
                                                    }}
                                                >
                                                    {b.logo ? (
                                                        <Box
                                                            component="img"
                                                            src={b.logo}
                                                            alt={b.name}
                                                            sx={{
                                                                width: 28,
                                                                height: 28,
                                                                objectFit: "contain",
                                                                borderRadius: "6px",
                                                                mr: 1.25,
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                    ) : null}
                                                    <ListItemText
                                                        primary={b.name}
                                                        secondary={b.desc}
                                                        primaryTypographyProps={{
                                                            fontFamily: "DM Sans",
                                                            color: "#0D1F3C",
                                                            fontSize: "0.92rem",
                                                            fontWeight: 600,
                                                        }}
                                                        secondaryTypographyProps={{
                                                            fontFamily: "DM Sans",
                                                            color: "rgba(55,65,81,0.75)",
                                                            fontSize: "0.75rem",
                                                            noWrap: true,
                                                        }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            )}
                        </React.Fragment>
                    ))}

                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => {
                                navigate("/offers");
                                handleDrawerToggle();
                            }}
                            sx={{ py: 1.5, borderBottom: '1px solid rgba(226,230,238,0.7)' }}
                        >
                            <ListItemText
                                primary={`🔥 Offers${liveOfferCount > 0 ? ` (${liveOfferCount} live)` : ''}`}
                                primaryTypographyProps={{ fontFamily: "DM Sans", color: "#B4232F", fontSize: "16px", fontWeight: 700 }}
                            />
                        </ListItemButton>
                    </ListItem>

                    {!isLoggedIn && (
                        <>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => {
                                navigate("/list-your-property");
                                handleDrawerToggle();
                            }}
                            sx={{
                                py: 1.5,
                                borderBottom: '1px solid rgba(226,230,238,0.7)',
                            }}
                        >
                            <ListItemText
                                primary="List Your Property"
                                primaryTypographyProps={{
                                    fontFamily: "DM Sans",
                                    color: "#374151",
                                    fontSize: "16px"
                                }}
                            />
                        </ListItemButton>
                    </ListItem>

                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => {
                                navigate("/travel-agents");
                                handleDrawerToggle();
                            }}
                            sx={{
                                py: 1.5,
                                borderBottom: '1px solid rgba(226,230,238,0.7)',
                            }}
                        >
                            <ListItemText
                                primary="Travel Agents"
                                primaryTypographyProps={{
                                    fontFamily: "DM Sans",
                                    color: "#374151",
                                    fontSize: "16px"
                                }}
                            />
                        </ListItemButton>
                    </ListItem>

                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => {
                                navigate("/contact-us");
                                handleDrawerToggle();
                            }}
                            sx={{
                                py: 1.5,
                                borderBottom: '1px solid rgba(226,230,238,0.7)',
                            }}
                        >
                            <ListItemText
                                primary="Contact Us"
                                primaryTypographyProps={{
                                    fontFamily: "DM Sans",
                                    color: "#374151",
                                    fontSize: "16px"
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                        </>
                    )}

                    {isLoggedIn && (
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={() => {
                                    navigate("/my-booking");
                                    handleDrawerToggle();
                                }}
                                sx={{
                                    py: 1.5,
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                }}
                            >
                                <ListItemText
                                    primary="My Booking"
                                    primaryTypographyProps={{
                                        fontFamily: "Inter",
                                        fontSize: "16px"
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    )}

                    {!isLoggedIn && (
                        <>
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => {
                                        handleDrawerToggle();
                                        handleGuestLogin();
                                    }}
                                    sx={{
                                        py: 1.5,
                                        borderBottom: '1px solid rgba(226,230,238,0.7)',
                                        background: "#E8304A",
                                        borderRadius: "8px",
                                        margin: "8px 16px",
                                        color: "#ffffff",
                                        fontWeight: 600,
                                        transition: "all 0.3s ease",
                                        '&:hover': {
                                            background: "#d72742",
                                            transform: "translateY(-1px)",
                                            boxShadow: "0 4px 12px rgba(232, 48, 74, 0.3)"
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary="Guest Login"
                                        primaryTypographyProps={{
                                            fontFamily: "DM Sans",
                                            fontSize: "16px",
                                            fontWeight: 600,
                                            color: "#ffffff"
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => {
                                        handleDrawerToggle();
                                        handleOwnerLogin();
                                    }}
                                    sx={{
                                        py: 1.5,
                                        borderBottom: '1px solid rgba(226,230,238,0.7)',
                                        borderRadius: "8px",
                                        margin: "0 16px 8px",
                                    }}
                                >
                                    <ListItemText
                                        primary="For Owner Login"
                                        primaryTypographyProps={{
                                            fontFamily: "DM Sans",
                                            fontSize: "16px",
                                            fontWeight: 600,
                                            color: "#0D1F3C"
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        </>
                    )}

                    {isLoggedIn && (
                        <>
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => {
                                        navigate("/profile");
                                        handleDrawerToggle();
                                    }}
                                    sx={{
                                        py: 1.5,
                                        borderBottom: '1px solid rgba(226,230,238,0.7)',
                                    }}
                                >
                                    <ListItemText
                                        primary="Profile"
                                        primaryTypographyProps={{
                                            fontFamily: "DM Sans",
                                            color: "#374151",
                                            fontSize: "16px"
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => {
                                        navigate("/my-booking");
                                        handleDrawerToggle();
                                    }}
                                    sx={{
                                        py: 1.5,
                                        borderBottom: '1px solid rgba(226,230,238,0.7)',
                                    }}
                                >
                                    <ListItemText
                                        primary="My Bookings"
                                        primaryTypographyProps={{
                                            fontFamily: "DM Sans",
                                            color: "#374151",
                                            fontSize: "16px"
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => {
                                        handleDrawerToggle();
                                        handleLogout();
                                    }}
                                    sx={{
                                        py: 1.5,
                                        borderBottom: '1px solid rgba(226,230,238,0.7)',
                                        color: '#E8304A',
                                    }}
                                >
                                    <LogoutOutlinedIcon sx={{ mr: 1.5, fontSize: 22 }} />
                                    <ListItemText
                                        primary="Log out"
                                        primaryTypographyProps={{
                                            fontFamily: "DM Sans",
                                            color: "#E8304A",
                                            fontSize: "16px",
                                            fontWeight: 600,
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        </>
                    )}
                </List>
            </Drawer>
        </>
    );
}

const mapStateToProps = (state) => ({
    publicBrands: state.brandPage?.list || [],
});

export default connect(mapStateToProps, { getPublicBrands })(NavbarComponent);


